import { ActivityMessageRenderer } from "@/components/conversations/activity-message-renderer";
import { EntityInteractionThumbnail } from "@/components/conversations/entity-interaction-message-renderer";
import { EntitySharedMessageRenderer } from "@/components/conversations/entity-shared-message-renderer";
import { TrainingSimulatorTagBadge } from "@/components/conversations/training-simulator-tag-badge";
import type { ChatRenderContext } from "@/components/shared/chat-message-types";
import { resolveTrainingSimulatorTag } from "@/lib/training-simulator/message-tags";

// Thin wrappers reusing the live renderers for activity/entity messages. The thread
// carries only `customType` + `rawData`, so map to the shape those renderers parse.
export interface AttentionSpecialSource {
  text: string;
  customType: string;
  rawData: string | null;
}

function toRendererMessage(source: AttentionSpecialSource): {
  text: string;
  customType: string;
  data?: string;
} {
  return {
    text: source.text,
    customType: source.customType,
    data: source.rawData ?? undefined,
  };
}

export function AttentionTextBadge({ rawData }: { rawData: string | null }) {
  const tag = resolveTrainingSimulatorTag(rawData);
  return tag ? <TrainingSimulatorTagBadge tag={tag} /> : null;
}

interface AttentionSpecialContentProps {
  source: AttentionSpecialSource;
  context?: ChatRenderContext;
}

export function AttentionActivityContent({
  source,
  context,
}: AttentionSpecialContentProps) {
  const tag = resolveTrainingSimulatorTag(source.rawData);
  if (tag) {
    return <TrainingSimulatorTagBadge tag={tag} />;
  }
  return (
    <ActivityMessageRenderer message={toRendererMessage(source)} context={context} />
  );
}

export function AttentionEntitySharedContent({
  source,
  context,
}: AttentionSpecialContentProps) {
  const tag = resolveTrainingSimulatorTag(source.rawData);
  if (tag) {
    return <TrainingSimulatorTagBadge tag={tag} />;
  }
  return (
    <EntitySharedMessageRenderer
      message={toRendererMessage(source)}
      context={context}
    />
  );
}

export function AttentionEntityInteractionContent({
  source,
  context,
}: AttentionSpecialContentProps) {
  const { text, ...message } = toRendererMessage(source);
  const tag = resolveTrainingSimulatorTag(source.rawData);
  // Parity with the old dispatcher: the simulator tag replaces the thumbnail,
  // keeping any caption body that accompanied it.
  const preBubble = tag ? (
    <TrainingSimulatorTagBadge tag={tag} />
  ) : (
    <EntityInteractionThumbnail message={message} context={context} />
  );
  return (
    <div className="flex flex-col gap-1">
      {preBubble}
      {text.length > 0 ? (
        <span className="px-1 font-body text-body-2 leading-4">{text}</span>
      ) : null}
    </div>
  );
}
